/**
 * pwRequest.component.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/

import * as React from "react";

import { Alert, Button, CircularProgress, FormControl, TextField } from "@mui/material";

import { EMAIL_REGEX } from "../models/auth";
import { authServiceFactory } from "../services/auth.service";

interface State {
  sending: boolean,
  success: boolean,
  email: string,
  emailErr: string,
  errMsg: string
}

export class PasswordRequestContainer extends React.Component<Record<string, never>, State> {
  constructor(props: Record<string, never>) {
    super(props);

    this.updateValue = this.updateValue.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    this.doRequest = this.doRequest.bind(this);

    this.state = {
      sending: false,
      success: false,
      email: "",
      emailErr: "",
      errMsg: ""
    };
  }

  private updateValue(event: React.SyntheticEvent) {
    const inputElem = event.target as HTMLInputElement;
    const propName = inputElem.name;
    if (!propName) {
      console.error("missing name on input:", inputElem.id);
      return;
    }
    const newValue = propName === "email" ? inputElem.value?.toLowerCase() :
      inputElem.value;
    const update: any = {[propName]: newValue};
    this.setState(update);
  }

  private onKeyUp(event: React.KeyboardEvent): void {
    if (event.key !== "Enter") { return; }
    const inputElem = event.target as HTMLInputElement;
    const propName = inputElem.name;
    if (!propName) {
      console.error("missing name on input:", inputElem.id);
      return;
    }
    if (propName === "email") {
      this.doRequest();
      event.preventDefault();
    }
  }

  private async doRequest() {
    try {
      if (!this.state.email) {
        this.setState({errMsg: "Please, enter an email address"});
        return;
      }
      if (!EMAIL_REGEX.test(this.state.email)) {
        this.setState({errMsg: "Please, enter a valid email"});
        return;
      }
      this.setState({sending: true});
      await authServiceFactory().sendPWRequest(this.state.email);
      this.setState({success: true});
    } catch (err) {
      console.error("Error sending request:", err);
      this.setState({errMsg: (err as Error).message});
    } finally {
      this.setState({sending: false});
    }
  }

  render(): React.ReactElement<any, any> {
    return <div className="main-section-inner password-request-container">
      <header className="welcome-heading">
        <div className="logo-img" />
      </header>
      {this.state.success ? this.renderSuccess() : this.renderForm()}
    </div>;
  }

  renderForm(): React.ReactElement<any, any> {
    return <section className="password-request-form">
      <form className="form-wrapper" onSubmit={this.doRequest}>
        { this.state.errMsg && <Alert
          variant="filled"
          severity="error">{this.state.errMsg}</Alert>}
        <header>
          <h2>Password Reset</h2>
          <p>Enter your email to continue.</p>
        </header>
        <div className="form-row">
          <FormControl fullWidth>
            <TextField id="email-input" label="Email" type="email" name="email" required
              variant="outlined"
              fullWidth
              value={this.state.email}
              onChange={this.updateValue}
              onKeyPress={this.onKeyUp} />
          </FormControl>
        </div>
        <div className="button-row">
          {this.state.sending ? <CircularProgress size={30} /> :
            <Button variant="contained"
              disabled={!this.state.email}
              onClick={this.doRequest}>Submit</Button>
          }
        </div>
      </form>
    </section>;
  }

  renderSuccess(): React.ReactElement<any, any> {
    return <section className="password-success-msg">
      <h2>Success!</h2>
      <p>If an account with that email exists, we've sent further
        instructions to your inbox!</p>
    </section>;
  }
}